// useChatSocket — WebSocket chat connection with auto-reconnect and graceful fallback.
//
// Usage:
//   const { connected } = useChatSocket(channelId, token, {
//     onNewMessage: (msg) => setMessages((prev) => [...prev, msg]),
//     onDeleteMessage: (id) => setMessages((prev) => prev.filter((m) => m.id !== id)),
//   });
//
// When `connected === true`, the component can rely on real-time events
// and drastically reduce polling frequency (or stop polling completely).
// On disconnect, it auto-reconnects with exponential backoff (1s, 2s, 4s, max 30s).
//
import { useEffect, useRef, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
// Convert https://… → wss://…  (and http://… → ws://…)
const WS_BASE = BACKEND.replace(/^http/, "ws");

export default function useChatSocket(channel, token, handlers = {}) {
  const { onNewMessage, onDeleteMessage } = handlers;
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const handlersRef = useRef({ onNewMessage, onDeleteMessage });

  // Keep handlers ref fresh without re-creating the WS on every render
  useEffect(() => {
    handlersRef.current = { onNewMessage, onDeleteMessage };
  }, [onNewMessage, onDeleteMessage]);

  useEffect(() => {
    if (!channel || !token || !WS_BASE) return undefined;

    let cancelled = false;

    const cleanup = () => {
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try { wsRef.current.close(1000, "client_closing"); } catch { /* ignore */ }
        wsRef.current = null;
      }
    };

    const connect = () => {
      if (cancelled) return;
      const url = `${WS_BASE}/api/ws/chat/${encodeURIComponent(channel)}?token=${encodeURIComponent(token)}`;
      let ws;
      try {
        ws = new WebSocket(url);
      } catch (e) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) { try { ws.close(); } catch { /* ignore */ } return; }
        reconnectAttemptRef.current = 0;
        setConnected(true);
        // Heartbeat ping every 25s to keep proxy connections alive
        pingTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try { ws.send("ping"); } catch { /* ignore */ }
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        if (typeof event.data !== "string") return;
        if (event.data === "pong") return;
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === "new_message" && handlersRef.current.onNewMessage) {
            handlersRef.current.onNewMessage(payload.data);
          } else if (payload.type === "delete_message" && handlersRef.current.onDeleteMessage) {
            handlersRef.current.onDeleteMessage(payload.data?.id);
          }
          // heartbeat → ignore
        } catch { /* ignore malformed frames */ }
      };

      ws.onerror = () => {
        // onclose will follow — backoff handled there
      };

      ws.onclose = () => {
        setConnected(false);
        if (pingTimerRef.current) {
          clearInterval(pingTimerRef.current);
          pingTimerRef.current = null;
        }
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (cancelled) return;
      reconnectAttemptRef.current += 1;
      const attempt = reconnectAttemptRef.current;
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s, capped at 30s
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 30000);
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    connect();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [channel, token]);

  return { connected };
}
